import { ChartOptions } from 'chart.js';
import chroma from 'chroma-js';

// Define strong types for common chart options
const COMMON_BAR_CHART_OPTIONS: ChartOptions<'bar'> = {
  maintainAspectRatio: false,
  responsive: true,
  layout: {
    padding: {
      top: 22,
    },
  },
  plugins: {
    tooltip: {
      callbacks: {
        label: function (context) {
          const datasetLabel = context.dataset.label || '';
          const label = context.label || '';
          const value = context.raw || 0;

          return `${datasetLabel}: ${label}: ${value}`;
        },
      },
      backgroundColor: '#f5f5f5',
      titleColor: '#333',
      bodyColor: '#666',
      bodySpacing: 4,
      padding: 12,
      mode: 'index',
      intersect: false,
      position: 'nearest',
    },
    datalabels: {
      color: 'black',
      borderColor: 'white',
      borderRadius: 5,
      backgroundColor: 'white',
      font: {
        weight: 'bold',
      },
      padding: 1,
      anchor: 'end',
      align: 'top',
      offset: 0,
    },
    legend: {
      display: false,
    },
  },
};

// Strongly typed bar chart options
export const BAR_CHART_OPTIONS: ChartOptions<'bar'> = {
  ...COMMON_BAR_CHART_OPTIONS,
  scales: {
    y: {
      grid: {
        drawBorder: false,
        color: 'rgba(29,140,248,0.1)', // Grid line color
        borderColor: 'rgba(29,140,248,1)', // Zero line color
      },
      min: 0, // Clamp to non-negative
      beginAtZero: true,
      ticks: {
        padding: 5,
        color: '#9e9e9e',
        precision: 0, // No decimal places
        // Only show non-negative integers
        callback: (value) => {
          const v = Number(value);
          return Number.isInteger(v) && v >= 0 ? v.toString() : '';
        },
        // Optional: uncomment to enforce integer tick steps (careful with large ranges)
        // stepSize: 1,
      },
    },
    x: {
      grid: {
        drawBorder: false,
        color: 'rgba(29,140,248,0.1)', // Grid line color
        borderColor: 'rgba(29,140,248,1)', // Zero line color
      },
      ticks: {
        padding: 10,
        color: '#9e9e9e',
        callback: function (value) {
          const label = this.getLabelForValue(Number(value)); // Get the original label
          return label?.length > 15 ? label.substring(0, 25) + '...' : label; // Truncate
        },
      },
    },
  },
  elements: {
    bar: {
      hoverBorderWidth: 2, // Added here for bar-specific hover border width
    },
  },
};

// Strongly typed stacked bar chart options
export const STACKED_BAR_CHART_OPTIONS: ChartOptions<'bar'> = {
  ...COMMON_BAR_CHART_OPTIONS,
  scales: {
    x: {
      stacked: true,
      grid: {
        drawBorder: false,
        color: 'rgba(29,140,248,0.1)', // Grid line color
        borderColor: 'rgba(29,140,248,1)', // Zero line color
      },
      ticks: {
        padding: 5,
        callback: function (value) {
          const label = this.getLabelForValue(Number(value)); // Get the original label
          return label?.length > 15 ? label.substring(0, 25) + '...' : label; // Truncate
        },
      },
    },
    y: {
      stacked: true,
      grid: {
        drawBorder: false,
        color: 'rgba(29,140,248,0.1)', // Grid line color
        borderColor: 'rgba(29,140,248,1)', // Zero line color
      },
      min: 0, // Clamp to non-negative
      beginAtZero: true,
      ticks: {
        padding: 10,
        color: '#9e9e9e',
        precision: 0, // No decimal places
        // Only show non-negative integers
        callback: (value) => {
          const v = Number(value);
          return Number.isInteger(v) && v >= 0 ? v.toString() : '';
        },
        // Optional: enforce integer steps
        // stepSize: 1,
      },
    },
  },
  plugins: {
    ...COMMON_BAR_CHART_OPTIONS.plugins,
    tooltip: {
      callbacks: undefined,
    },
  },
  datasets: {
    bar: {
      maxBarThickness: 40, // Moved here to the dataset configuration
    },
  },
};

// Strongly typed doughnut chart options
/**
 * Configuration options for a Doughnut chart using Chart.js.
 *
 * This configuration is designed to provide a visually appealing and accessible
 * doughnut chart with custom styling, tooltips, and data labels. It includes
 * options for responsiveness, interactivity, and data-driven customization.
 *
 * Key Features:
 * - **Responsive Design**: Ensures the chart adjusts to the container size.
 * - **Cutout Percentage**: Creates a "donut" effect by cutting out the center.
 * - **Hover Effects**: Highlights segments with a border on hover.
 * - **Data Labels**: Displays percentage values inside the chart segments.
 *   - Dynamically adjusts text color for contrast against the background.
 *   - Hides labels for segments with a value of 0.
 * - **Legend**: Displays a legend on the right side of the chart.
 * - **Tooltips**: Provides detailed information on hover, including dataset label,
 *   segment label, and value.
 * - **Arc Styling**: Adds a white border around each segment for better separation.
 *
 * Customization Details:
 * - **Data Labels Background**: Adjusts the background color of data labels
 *   based on the segment's color. Transparent background is applied for segments
 *   with a value of 0.
 * - **Data Labels Text Color**: Ensures text is readable by calculating contrast
 *   against the segment's background color.
 * - **Percentage Formatter**: Calculates and displays the percentage of each
 *   segment relative to the total sum of data values.
 *
 * Usage:
 * This configuration can be passed to a Chart.js Doughnut chart instance to
 * render a chart with the specified options.
 */
export const DOUGHNUT_CHART_OPTIONS: ChartOptions<'doughnut'> = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: '20%',
  plugins: {
    datalabels: {
      backgroundColor: function (context) {
        const index = context.dataIndex;
        const value = context.dataset.data[index];
        let colors = context.dataset.backgroundColor;
        if (value === 0 && Array.isArray(colors) && colors.length > index) {
          colors = Array.isArray(context.dataset.backgroundColor)
            ? [...context.dataset.backgroundColor]
            : [];
          const mutableColors = Array.isArray(colors) ? [...colors] : [];
          mutableColors[index] = chroma(mutableColors[index]).alpha(0).css();
          colors = mutableColors;
        }
        return Array.isArray(colors) && colors.length > index
          ? colors[index]
          : null;
      },
      color: function (context) {
        const backgroundColor = context.dataset.backgroundColor;
        if (
          Array.isArray(backgroundColor) &&
          backgroundColor.length > context.dataIndex
        ) {
          const bgColor = backgroundColor[context.dataIndex];
          return chroma.contrast(bgColor, 'white') > 4.5 ? 'white' : 'black';
        } else if (typeof backgroundColor === 'string') {
          return chroma.contrast(backgroundColor, 'white') > 4.5
            ? 'white'
            : 'black';
        }
        return 'black'; // Default color if no valid background color is found
      },
      borderColor: 'white',
      borderRadius: 10,
      borderWidth: 0,
      font: {
        weight: 'bold',
      },
      padding: 2,
      formatter: (value, ctx) => {
        let sum = 0;
        const dataArr = ctx.dataset.data;
        dataArr.forEach((data) => {
          if (typeof data === 'number' && data !== null) {
            sum += data;
          }
        });
        const amount = sum > 0 ? ((value * 100) / sum).toFixed(0) : '0';
        return amount === '0' ? '' : `${amount}%`;
      },
    },
    legend: {
      display: true,
      position: 'right',
    },
    tooltip: {
      callbacks: {
        label: function (context) {
          const datasetLabel = context.dataset.label || '';
          const label = context.label || '';
          const value = context.raw || 0;

          return `${datasetLabel}: ${label}: ${value}`;
        },
      },
      backgroundColor: '#f5f5f5',
      titleColor: '#333',
      bodyColor: '#666',
      bodySpacing: 4,
      padding: 12,
    },
  },
  elements: {
    arc: {
      borderWidth: 2,
      borderColor: 'white',
      hoverBorderWidth: 2, // Moved here to the arc configuration
    },
  },
};
